import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoopBackAuth, MemberApi, OrganisationsApi } from '@careapp/angular-sdk';
import { ADMIN_ROLE, DASHBOARD_ROLES } from '@careapp/core';
import { Actions, Effect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  forkJoin as observableForkJoin,
  from as observableFrom,
  Observable,
  of as observableOf
} from 'rxjs';
import 'rxjs/observable/forkJoin';
import 'rxjs/observable/from';
import { switchMap, withLatestFrom } from 'rxjs/operators';
import * as actions from '../actions';
import { FORCE_LOGOUT_401 } from '../actions';
import * as selectors from '../reducers';

export const NO_ORGANISATIONS = 'NO_ORGANISATIONS';
export const NO_USER = 'NO_USER';
export const NO_ROLES = 'NO_ROLES';
export const FAIL_TOKEN_REFRESH = 'FAIL_TOKEN_REFRESH';
const DAY_MS = 1000 * 60 * 60 * 24;

@Injectable()
export class StartupEffects {
  @Effect()
  applicationStartup = this.actions$
    .ofType(actions.RESPONSE_SUCCESS_AUTH_LOGIN, actions.RESPONSE_REHYDRATED)
    .pipe(
      withLatestFrom(
        this.store.select(selectors.auth.getAuth),
        this.store.select(selectors.getSelectedId('organisations')),
        this.store.select(selectors.auth.getRoles),
        this.store.select(selectors.auth.getLoggedInUser)
      ),
      switchMap(
        ([action, auth, selectedOrgId, roles, user]): Observable<any> => {
          if (!auth) {
            // We're at the login screen and don't have auth data yet - nothing to do
            return observableOf(new actions.ApplicationReady());
          } else {
            this.loopbackAuth.setToken(<any>auth);
          }
          return this.refreshTokenIfRequired(auth).switchMap(updatedTokenAction =>
            this.performStartupFetches(auth.userId).pipe(
              switchMap(results =>
                this.createObservableStreamFromResults(results, updatedTokenAction, selectedOrgId)
              )
            )
          );
        }
      )
    );

  constructor(
    private actions$: Actions,
    private memberApi: MemberApi,
    private store: Store<any>,
    private orgsApi: OrganisationsApi,
    private loopbackAuth: LoopBackAuth,
    private router: Router
  ) { }

  createObservableStreamFromResults(
    results: any[],
    updatedTokenAction: any,
    selectedOrgId: string
  ) {
    const failure = results.find(res => res.type === FORCE_LOGOUT_401);
    const actionStream: any[] = results.concat([
      updatedTokenAction,
      new actions.ApplicationReady()
    ]);
    const [memberResults, roleResults, orgResults] = results;
    if (failure) {
      return observableFrom(actionStream);
    }
    if (!roleResults.payload.includes(ADMIN_ROLE)) {
      // Filter orgs based on user permissions (exclude non dashboard roles)
      const authorizedOrgs = orgResults.payload.filter(org => {
        const dashboardRoles = org.roles.filter(role => DASHBOARD_ROLES.includes(role));
        return !!dashboardRoles.length;
      });
      if (!authorizedOrgs.length) {
        return observableFrom(actionStream.concat(new actions.ForceLogout401(NO_ORGANISATIONS)));
      }
    }
    const orgId = selectedOrgId || orgResults.payload[0].id;
    return observableFrom(
      actionStream.concat(
        new actions.ApplicationAuthorized({
          user: memberResults.payload,
          orgId,
          roles: roleResults.payload
        })
      )
    );
  }

  performStartupFetches(userId: string) {
    return observableForkJoin(
      this.memberApi
        .findById(userId)
        .map((updatedUser: any) => new actions.ResponseSuccessLoggedInUser(updatedUser))
        .catch(err => observableOf(new actions.ForceLogout401(NO_USER))),
      this.memberApi
        .listRoles(userId)
        .map(updatedRoles => new actions.ResponseSuccessUserRoles(updatedRoles))
        .catch(err => observableOf(new actions.ForceLogout401(NO_ROLES))),
      this.orgsApi
        .find()
        .map(organisations => actions.Organisations.ResponseSuccessOwn(organisations))
        .catch(err => observableOf(new actions.ForceLogout401(NO_ORGANISATIONS)))
    );
  }

  refreshTokenIfRequired(auth: any) {
    let msSinceCreated = DAY_MS;
    if (auth.created && auth.created.getTime) {
      msSinceCreated = Date.now() - auth.created.getTime();
    } else {
      return observableOf(new actions.ForceLogout401(FAIL_TOKEN_REFRESH));
    }
    if (msSinceCreated > DAY_MS || auth.dirty) {
      return this.memberApi
        .refreshToken(auth.userId, !auth.dirty)
        .map(updatedToken => {
          this.loopbackAuth.setToken(<any>updatedToken);
          return new actions.ResponseSuccessAuthRefreshAction(updatedToken);
        })
        .catch(ex => {
          if (ex.statusCode === 401) {
            return observableFrom([
              new actions.ResponseFailAuthRefreshAction(ex),
              new actions.ForceLogout401(FAIL_TOKEN_REFRESH)
            ]);
          } else {
            // Failed to refresh for other reason - use stale token.
            return observableOf(new actions.ResponseFailAuthRefreshAction(auth));
          }
        });
    } else {
      return observableOf(new actions.ResponseSuccessAuthRefreshAction(auth));
    }
  }
}
